import React from "react";
import { tokens } from "@blurb/codex-react";
import { ICON_CODEPOINTS, type IconName } from "./iconCodepoints.generated";

// A hidden icon cannot carry a label, and a labelled one must not be hidden —
// expressed as a union so the contradictory pair does not compile. Both spellings
// of each boolean are accepted because JSX `aria-hidden="true"` is a string.
type IconV3A11y =
  | { "aria-hidden"?: true | "true"; "aria-label"?: never }
  | { "aria-hidden": false | "false"; "aria-label": string };

export type IconV3Props = {
  icon: IconName;
  size?: string | number;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
} & IconV3A11y;

// See developer integration guide for more info: https://developers.google.com/fonts/docs/material_symbols
//
// `icon` is limited to the glyphs bundled in the subsetted font — add new ones to
// icon-names.json and run `npm run generate-icons` in this package. The glyph is
// emitted as a codepoint because the font is subsetted by codepoint, so the
// ligature names the upstream font uses would not resolve.
export const IconV3: React.FC<IconV3Props> = ({
  icon,
  size = 24,
  color,
  className,
  style,
  "aria-hidden": ariaHidden = true,
  "aria-label": ariaLabel,
}) => {
  const finalColor = color || tokens.color.semantic.icon.bold;
  const hidden = ariaHidden !== false && ariaHidden !== "false";
  return (
    <span
      className={`material-symbols-rounded ${className || ""}`}
      style={{
        fontSize: size,
        color: finalColor,
        display: "inline-block",
        lineHeight: 1,
        ...style,
      }}
      aria-hidden={hidden}
      aria-label={ariaLabel}
      role={hidden ? undefined : "img"}
    >
      {String.fromCodePoint(ICON_CODEPOINTS[icon])}
    </span>
  );
};
